import { useState } from "react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Search, ArrowUpDown, Star, MapPin } from "lucide-react";
import { Slider } from "../ui/slider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose
} from "../ui/sheet";
import { Badge } from "../ui/badge";
import {FilterOptions, RestaurantFiltersProps} from "../../types";

const cuisines = [
  { value: "all", label: "All cuisines" },
  { value: "italian", label: "Italian" },
  { value: "french", label: "French" },
  { value: "japanese", label: "Japanese" },
  { value: "mediterranean", label: "Mediterranean" },
  { value: "indian", label: "Indian" },
  { value: "steakhouse", label: "Steakhouse" },
];

const sortOptions = [
  { value: "none", label: "Default" },
  { value: "rating", label: "Rating" },
  { value: "distance", label: "Distance" },
  { value: "name", label: "Name" },
];

const RestaurantFilters = ({ on_filter_change, total_results = 0 }: RestaurantFiltersProps) => {
  // Filter state
  const [search, setSearch] = useState("");
  const [cuisine, setCuisine] = useState("all");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50]);
  const [sortBy, setSortBy] = useState<"rating" | "distance" | "name" | "none">("none");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [minRating, setMinRating] = useState<number>(0);
  const [maxDistance, setMaxDistance] = useState<number>(30);

  // Count active filters (excluding search and cuisine which are always shown)
  const activeFilterCount = [
    priceRange[0] > 0 || priceRange[1] < 50,
    sortBy !== "none",
    minRating > 0,
    maxDistance < 30
  ].filter(Boolean).length;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    updateFilters({ search: e.target.value });
  };

  const handleCuisineChange = (value: string) => {
    setCuisine(value);
    updateFilters({ cuisine: value });
  };

  const handlePriceRangeChange = (value: [number, number]) => {
    setPriceRange(value);
    updateFilters({
      price_range: {
        min: value[0],
        max: value[1]
      }
    });
  };

  const handleSortChange = (value: string) => {
    setSortBy(value as "rating" | "distance" | "name" | "none");
    updateFilters({ sort_by: value as "rating" | "distance" | "name" | "none" });
  };

  const handleSortOrderToggle = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    updateFilters({ sort_order: newOrder });
  };

  const handleMinRatingChange = (value: number[]) => {
    setMinRating(value[0]);
    updateFilters({ min_rating: value[0] });
  };

  const handleMaxDistanceChange = (value: number[]) => {
    setMaxDistance(value[0]);
    updateFilters({ max_distance: value[0] });
  };

  const updateFilters = (newFilters: Partial<FilterOptions>) => {
    on_filter_change({
      search,
      cuisine,
      price_range: { min: priceRange[0], max: priceRange[1] },
      sort_by: sortBy,
      sort_order: sortOrder,
      min_rating: minRating,
      max_distance: maxDistance,
      ...newFilters
    });
  };

  const resetFilters = () => {
    setSearch("");
    setCuisine("all");
    setPriceRange([0, 50]);
    setSortBy("none");
    setSortOrder("asc");
    setMinRating(0);
    setMaxDistance(30);

    on_filter_change({
      search: "",
      cuisine: "all",
      price_range: { min: 0, max: 50 },
      sort_by: "none",
      sort_order: "asc",
      min_rating: 0,
      max_distance: 30
    });
  };

  return (
    <div className="mb-8">
      <div className="mb-8 flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h2 className="mb-4 font-serif text-3xl font-bold text-gray-800 dark:text-white">
            Explore Restaurants
          </h2>
          <p className="max-w-2xl text-gray-600 dark:text-gray-300">
            Discover participating restaurants offering exclusive surprise meals.
          </p>
          {total_results > 0 && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium text-primary">{total_results}</span> {total_results === 1 ? 'restaurant' : 'restaurants'}
            </p>
          )}
        </div>

        <div className="mt-6 flex flex-col md:mt-0 md:flex-row md:items-center md:space-x-3">
          {/* Sort options (Desktop) */}
          <div className="hidden md:flex md:items-center md:space-x-2">
            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {sortBy !== "none" && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleSortOrderToggle}
                className="h-10 w-10"
              >
                <ArrowUpDown
                  className={`h-4 w-4 ${
                    sortOrder === "desc" ? "rotate-180 transform" : ""
                  } transition-transform duration-200`}
                />
              </Button>
            )}
          </div>

          {/* Filter button for advanced filters */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-lg">
              <SheetHeader className="mb-6">
                <SheetTitle>Advanced Filters</SheetTitle>
                <SheetDescription>
                  Refine your search to find the perfect surprise meal.
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6">
                {/* Search */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Search</h3>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search by name, location or cuisine..."
                      value={search}
                      onChange={handleSearchChange}
                      className="w-full pr-10"
                    />
                    <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                  </div>
                </div>

                {/* Cuisine Filter */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Cuisine</h3>
                  <Select value={cuisine} onValueChange={handleCuisineChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Filter by cuisine" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuisines.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range Slider */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Price Range</h3>
                    <p className="text-sm text-gray-500">
                      €{priceRange[0]} - €{priceRange[1]}
                    </p>
                  </div>
                  <Slider
                    defaultValue={[0, 50]}
                    max={50}
                    step={1}
                    value={priceRange}
                    onValueChange={(value) => handlePriceRangeChange(value as [number, number])}
                    className="py-4"
                  />
                </div>

                {/* Minimum Rating */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center text-sm font-medium">
                      <Star className="mr-2 h-4 w-4" />
                      Minimum Rating
                    </h3>
                    <p className="text-sm text-gray-500">{minRating.toFixed(1)}</p>
                  </div>
                  <Slider
                    defaultValue={[0]}
                    min={0}
                    max={5}
                    step={0.5}
                    value={[minRating]}
                    onValueChange={handleMinRatingChange}
                    className="py-4"
                  />
                </div>

                {/* Maximum Distance */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center text-sm font-medium">
                      <MapPin className="mr-2 h-4 w-4" />
                      Maximum Distance
                    </h3>
                    <p className="text-sm text-gray-500">{maxDistance} km</p>
                  </div>
                  <Slider
                    defaultValue={[30]}
                    min={1}
                    max={30}
                    step={1}
                    value={[maxDistance]}
                    onValueChange={handleMaxDistanceChange}
                    className="py-4"
                  />
                </div>

                {/* Sort options for mobile */}
                <div className="space-y-2 md:hidden">
                  <h3 className="text-sm font-medium">Sort By</h3>
                  <div className="flex items-center space-x-2">
                    <Select value={sortBy} onValueChange={handleSortChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {sortBy !== "none" && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleSortOrderToggle}
                        className="h-10 w-10"
                      >
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortOrder === "desc" ? "rotate-180 transform" : ""
                          } transition-transform duration-200`}
                        />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4">
                  <Button
                    variant="outline"
                    onClick={resetFilters}
                  >
                    Reset All
                  </Button>
                  <SheetClose asChild>
                    <Button>Apply Filters</Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active filters display */}
      {activeFilterCount > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Active filters:</span>

          {cuisine !== "all" && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Cuisine: {cuisines.find(c => c.value === cuisine)?.label}</span>
              <button
                onClick={() => {
                  setCuisine("all");
                  updateFilters({ cuisine: "all" });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Remove filter</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>
                </svg>
              </button>
            </Badge>
          )}

          {(priceRange[0] > 0 || priceRange[1] < 50) && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Price: €{priceRange[0]} - €{priceRange[1]}</span>
              <button
                onClick={() => {
                  setPriceRange([0, 50]);
                  updateFilters({ price_range: { min: 0, max: 50 } });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Remove filter</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>
                </svg>
              </button>
            </Badge>
          )}

          {minRating > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Min Rating: {minRating.toFixed(1)}</span>
              <button
                onClick={() => {
                  setMinRating(0);
                  updateFilters({ min_rating: 0 });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Remove filter</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>
                </svg>
              </button>
            </Badge>
          )}

          {maxDistance < 30 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Max Distance: {maxDistance} km</span>
              <button
                onClick={() => {
                  setMaxDistance(30);
                  updateFilters({ max_distance: 30 });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Remove filter</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>
                </svg>
              </button>
            </Badge>
          )}

          {sortBy !== "none" && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>
                Sort: {sortOptions.find(s => s.value === sortBy)?.label} ({sortOrder === "asc" ? "Asc" : "Desc"})
              </span>
              <button
                onClick={() => {
                  setSortBy("none");
                  setSortOrder("asc");
                  updateFilters({ sort_by: "none", sort_order: "asc" });
                }}
                className="ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Remove filter</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path><path d="m6 6 12 12"></path>
                </svg>
              </button>
            </Badge>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="ml-auto text-sm font-medium text-primary"
          >
            Clear All
          </Button>
        </div>
      )}
    </div>
  );
};

export default RestaurantFilters;
